import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import * as R from 'ramda'
import { Defaults } from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import { OrderType } from '~/constants/SOAPStates'
import { clearSearchOrders, searchOrders } from '~/store/actions/orders'
import {
  getFeatureToggle,
  getInventoryCategory,
  getSearchCatalogFiltersAndFacetsEnabled,
} from '~/store/reducers/constants'
import {
  getAppliedPatientRestrictions,
  getPatientRestrictions,
} from '~/store/reducers/orders'
import {
  getAppointmentId,
  getClientId,
  getOrderFilters,
  getPatientId,
  getSoapId,
} from '~/store/reducers/soap'
import {
  ChargeQueryParams,
  DEFAULT_ORDER_LIST_TYPES,
  OrderListType,
} from '~/types'

interface UseChargeInlineSearch {
  /** When SOAP slice has no client (e.g. estimate in Invoice), pass from parent */
  clientId?: string
  onFocus: () => void
  onSearch: (searchAttempt: string) => void
  patientId?: string
  validate: (() => boolean) | undefined
}

export const useChargeInlineSearch = ({
  validate,
  clientId: clientIdProp,
  patientId: patientIdProp,
  onSearch,
  onFocus,
}: UseChargeInlineSearch) => {
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()

  const appointmentId = useSelector(getAppointmentId)
  const soapId = useSelector(getSoapId)
  const soapClientId = useSelector(getClientId)
  const orderFilters = useSelector(getOrderFilters)
  const appliedPatientRestrictions = useSelector(getAppliedPatientRestrictions)
  const patientRestrictions = useSelector(getPatientRestrictions)
  const soapPatientId = useSelector(getPatientId)
  const clientId = clientIdProp ?? soapClientId
  const patientId = patientIdProp ?? soapPatientId
  const isSearchCatalogFiltersAndFacetsEnabled: boolean = useSelector(
    getSearchCatalogFiltersAndFacetsEnabled,
  )
  const isAwoChargesR53 = useSelector(
    getFeatureToggle(FeatureToggle.AWO_CHARGES_R5_3),
  )

  // Filter orderFilters to only include BUNDLE, PROCEDURE, LAB_TEST
  // Should be a backend fix once the old functionality is deprecated. From logs, the other types throws errors
  const filteredOrderFilters = orderFilters.filter((filter) =>
    [OrderType.BUNDLE, OrderType.PROCEDURE, OrderType.LAB_TEST].includes(
      filter.type as OrderType,
    ),
  )

  const InventoryCategory = useSelector(getInventoryCategory)
  const defaultInventoryCategoryIds = InventoryCategory.map(
    (category) => category.id,
  )

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get(ChargeQueryParams.CHARGE_SEARCH_QUERY) ?? '',
  )
  const [searchDisabled, setSearchDisabled] = useState(false)

  const onSearchChange = useCallback(
    (value: string) => {
      if (!clientId || !patientId) {
        return
      }

      // Update URL params using the established pattern to prevent scroll reset
      const url = new URL(window.location.href)
      if (value === '' && isAwoChargesR53) {
        url.searchParams.delete(ChargeQueryParams.CHARGE_SEARCH_QUERY)
      } else {
        url.searchParams.set(ChargeQueryParams.CHARGE_SEARCH_QUERY, value)
      }
      window.history.replaceState(
        { ...window.history.state, url: url.href },
        '',
        url.href,
      )
      setSearchTerm(value)

      if (value.length < Defaults.MIN_SEARCH_LENGTH) {
        dispatch(clearSearchOrders())
        return
      }

      onSearch(value)

      dispatch(
        searchOrders({
          ...(isSearchCatalogFiltersAndFacetsEnabled
            ? {
                addAggregations: true,
                patientRestrictions,
                listTypes: DEFAULT_ORDER_LIST_TYPES,
                inventoryCategoryIds: defaultInventoryCategoryIds,
                filters: R.pluck('type', filteredOrderFilters),
              }
            : {
                applyPatientRestriction: appliedPatientRestrictions,
                patientRestrictions: undefined,
                addAggregations: undefined,
                listTypes: undefined,
                inventoryCategoryIds: undefined,
                filters: R.pluck('type', orderFilters),
              }),
          eventId: appointmentId,
          clientId,
          forShipments: false,
          listType: OrderListType.FULL,
          patientId,
          searchTerm: value,
          withTasks: true,
          isSOAP: true,
          soapId,
          appointmentId,
        }),
      )
    },
    [
      appointmentId,
      clientId,
      appliedPatientRestrictions,
      isAwoChargesR53,
      isSearchCatalogFiltersAndFacetsEnabled,
      patientRestrictions,
      filteredOrderFilters,
      orderFilters,
      patientId,
      soapId,
      onSearch,
      dispatch,
    ],
  )

  const onSearchBlur = useCallback(() => {
    setSearchDisabled(false)
  }, [])
  const onSearchFocus = useCallback(() => {
    onFocus()
    setSearchDisabled(!validate?.())
  }, [])

  return {
    clientId,
    onSearchChange,
    onSearchBlur,
    onSearchFocus,
    patientId,
    searchDisabled,
    searchTerm,
  }
}
